import { IAddToCartItemAttribute } from '@/interfaces/cart';

export interface IBundleBuilderItem {
  key: string; // `${productId}_${size}` — same product, different size, is a different entry
  productId: string;
  seName: string;
  name: string;
  imageUrl: string;
  sku: string;
  size: string | number;
  unitPrice: number;
  quantity: number;
  attributes: IAddToCartItemAttribute[];
}

export interface IBundleBuilderSlabState {
  slabId: string;
  itemsByGroupId: Record<string, IBundleBuilderItem[]>;
  // Key of the item most recently auto-added from a PDP "Explore Bundle Offers" visit (if any),
  // so a later visit can replace it instead of stacking alongside it. Never touches anything the
  // customer added themselves through the product picker.
  seededItemKey?: string;
}

const STATE_KEY_PREFIX = 'bundle-builder-state:';
const storageKey = (slabId: string) => `${STATE_KEY_PREFIX}${slabId}`;

const isBrowser = () => typeof window !== 'undefined';

export const getSlabState = (slabId: string): IBundleBuilderSlabState | null => {
  if (!isBrowser()) return null;
  try {
    const raw = localStorage.getItem(storageKey(slabId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const groups = parsed?.itemsByGroupId;
    if (!groups || typeof groups !== 'object' || Array.isArray(groups)) return null;
    return parsed as IBundleBuilderSlabState;
  } catch {
    return null;
  }
};

export const saveSlabState = (state: IBundleBuilderSlabState): void => {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(storageKey(state.slabId), JSON.stringify(state));
  } catch {
    // Quota exceeded / storage blocked: the in-memory result is still returned to the caller.
  }
};

const getOrSeedState = (slabId: string): IBundleBuilderSlabState => getSlabState(slabId) ?? { slabId, itemsByGroupId: {} };

export const addItemToGroup = (slabId: string, groupId: string, item: IBundleBuilderItem): IBundleBuilderSlabState => {
  const state = getOrSeedState(slabId);
  const groupItems = state.itemsByGroupId[groupId] ? [...state.itemsByGroupId[groupId]] : [];
  const existingIndex = groupItems.findIndex((existing) => existing.key === item.key);

  if (existingIndex > -1) {
    groupItems[existingIndex] = { ...groupItems[existingIndex], quantity: groupItems[existingIndex].quantity + item.quantity };
  } else {
    groupItems.push(item);
  }

  const nextState: IBundleBuilderSlabState = { ...state, itemsByGroupId: { ...state.itemsByGroupId, [groupId]: groupItems } };
  saveSlabState(nextState);
  return nextState;
};

export const removeItemFromGroup = (slabId: string, groupId: string, itemKey: string): IBundleBuilderSlabState => {
  const state = getOrSeedState(slabId);
  const groupItems = state.itemsByGroupId[groupId] ?? [];

  const nextState: IBundleBuilderSlabState = {
    ...state,
    itemsByGroupId: { ...state.itemsByGroupId, [groupId]: groupItems.filter((item) => item.key !== itemKey) },
  };
  saveSlabState(nextState);
  return nextState;
};

export const setItemQuantity = (slabId: string, groupId: string, itemKey: string, quantity: number): IBundleBuilderSlabState => {
  const state = getOrSeedState(slabId);
  const groupItems = state.itemsByGroupId[groupId] ?? [];
  const safeQuantity = Math.max(1, quantity);

  const nextState: IBundleBuilderSlabState = {
    ...state,
    itemsByGroupId: {
      ...state.itemsByGroupId,
      [groupId]: groupItems.map((item) => (item.key === itemKey ? { ...item, quantity: safeQuantity } : item)),
    },
  };
  saveSlabState(nextState);
  return nextState;
};

// Applies the PDP-seeded item into a group, replacing the previous seed.
// If the customer already has the same key, nothing is recorded as seeded so a later seed can't delete their item.
export const applySeedItem = (slabId: string, groupId: string, item: IBundleBuilderItem): IBundleBuilderSlabState => {
  const state = getOrSeedState(slabId);
  const itemsByGroupId: Record<string, IBundleBuilderItem[]> = {};
  Object.entries(state.itemsByGroupId).forEach(([id, items]) => {
    itemsByGroupId[id] = state.seededItemKey ? items.filter((existing) => existing.key !== state.seededItemKey) : [...items];
  });

  const groupItems = itemsByGroupId[groupId] ?? [];
  const alreadyPresent = groupItems.some((existing) => existing.key === item.key);
  if (!alreadyPresent) groupItems.push(item);
  itemsByGroupId[groupId] = groupItems;

  const nextState: IBundleBuilderSlabState = { ...state, itemsByGroupId, seededItemKey: alreadyPresent ? undefined : item.key };
  saveSlabState(nextState);
  return nextState;
};

export const clearSlabState = (slabId: string): void => {
  if (!isBrowser()) return;
  localStorage.removeItem(storageKey(slabId));
};

// Drops stored picks for slabs that are no longer active. Call only after a successful slabs fetch.
export const pruneSlabStates = (activeSlabIds: string[]): void => {
  if (!isBrowser()) return;
  const active = new Set(activeSlabIds.map(storageKey));
  Object.keys(localStorage)
    .filter((key) => key.startsWith(STATE_KEY_PREFIX) && !active.has(key))
    .forEach((key) => localStorage.removeItem(key));
};

export const clearGroup = (slabId: string, groupId: string): IBundleBuilderSlabState => {
  const state = getOrSeedState(slabId);
  const nextState: IBundleBuilderSlabState = { ...state, itemsByGroupId: { ...state.itemsByGroupId, [groupId]: [] } };
  saveSlabState(nextState);
  return nextState;
};
